import {
  KLOPF_ABHAKBAR,
  KLOPF_MAX_ZEILEN,
  KLOPF_WORT,
  type Klopfmeldung,
} from '@netcon-work/shared'
import { useState } from 'react'
import { cn } from '../../lib/cn'
import { AufgabenVerweis, useAufgabeOeffnen } from '../AufgabeKontext'
import { Button } from '../ui/primitives'

/**
 * **Zone 0 – das Gegenstück zu „Ruhig gestellt"** ([Z-02](../../../../../docs/phasen/zusatz-02-mein-tag.md)).
 *
 * „Ruhig gestellt" schiebt weg, was heute keine Entscheidung braucht. Dann muss es eine Stelle
 * geben, die das Gegenteil tut – sonst landet Dringendes in derselben Ruhe wie alles andere.
 *
 * ## Die Regel, an der alles hängt
 *
 * **Die Zone ist normalerweise nicht da.** Kein leerer Kasten, keine „0 Meldungen": bei nichts
 * zu tun gibt die Komponente `null` zurück und die Seite fängt wieder mit „Jetzt" an. Nur so
 * bleibt der rote Streifen glaubwürdig. Eine Zone, die täglich da ist, ist Hintergrund.
 *
 * Drei weitere Sperren gegen die Dauerröte: höchstens {@link KLOPF_MAX_ZEILEN} Zeilen (der Rest
 * wird gezählt, nicht ausgebreitet), genau **ein** Knopf je Zeile, und eine enge Aufnahmeregel –
 * sie steht in `shared/meintag.ts` und lässt nur fünf Sorten herein.
 *
 * ## Die Erwähnung bekommt ihren eigenen Namen (28.08.2026, Z-11)
 *
 * Nils zur Zone darunter: *„sonst sieht der ja so Kommentare nicht unbedingt … die rücken ja nie
 * hoch."* Beim Nachsehen zeigte sich: **sie rücken hoch** – `lage.ts` lässt jede Erwähnung
 * herein, gleich ob die Aufgabe bei mir läuft. Aber sie stand hier als „ANTWORT · X hat
 * geantwortet" und war damit von einem beiläufigen Kommentar nicht zu unterscheiden. Eine
 * Funktion, die niemand erkennt, gibt es für den Benutzer nicht. Seitdem: eigenes Wort, eigener
 * Rang – oberhalb der Frist, weil dort ein Mensch wartet und hier nur eine Uhr läuft.
 *
 * ## Der Haken bei „Antwort" (25.08.2026, [E-229](../../../../../docs/ENTSCHEIDUNGEN.md#e-229))
 *
 * Nils: *„Kann ich die hier auch als «Gelesen» markieren?"* Vorher ging das nur im
 * Aktivitätsfeed – man las den Kommentar über „Ansehen", und die rote Zeile stand danach
 * unverändert da. Eine Zone, die man nicht leeren kann, wird zur Tapete.
 *
 * Ein **Haken**, kein zweiter Knopf: die Ein-Knopf-Regel unten meint Knöpfe, die *entscheiden*
 * („Ziehen", „Abnehmen"). Abhaken ist keine Entscheidung über die Aufgabe, sondern eine Notiz
 * über den Leser – dasselbe Feld wie im Feed, dieselbe Optik, derselbe Serverstand
 * (`feed_gelesen`). Und nur bei den Nachrichten-Arten ({@link KLOPF_ABHAKBAR}): eine Frist ist
 * nicht erledigt, weil man sie gelesen hat.
 */
export function KlopftAn({
  meldungen,
  ziehen,
  gelesenSetzen,
}: {
  meldungen: readonly Klopfmeldung[]
  /** „Sofort" hat als Einzige einen Knopf, der etwas verändert statt zu öffnen. */
  ziehen: (taskId: string) => void
  /** Haken setzen oder zurücknehmen. `bis` ist der Zeitpunkt der Antwort in dieser Zeile. */
  gelesenSetzen: (v: { taskId: string; bis: string; gelesen: boolean }) => void
}) {
  const oeffnen = useAufgabeOeffnen()
  const [alle, setAlle] = useState(false)

  if (meldungen.length === 0) return null

  const sichtbar = alle ? meldungen : meldungen.slice(0, KLOPF_MAX_ZEILEN)
  const rest = meldungen.length - sichtbar.length
  /**
   * **Gezählt wird, was noch offen ist.** Eine abgehakte Zeile steht im Nachlauf noch da, aber
   * sie wartet nicht mehr – sie mitzuzählen hieße, die Zahl über der Zone stimmt genau dann
   * nicht, wenn man gerade etwas dagegen getan hat.
   */
  const offen = meldungen.filter((m) => !m.gelesen).length

  return (
    <section className="mb-[22px]">
      <div className="mb-[9px] flex items-baseline gap-[9px]">
        <h3 className="text-[12px] font-bold tracking-[0.07em] uppercase">Klopft an</h3>
        <span className="text-xs text-muted">
          {offen === 0
            ? 'abgehakt – blendet sich gleich aus'
            : `${offen === 1 ? 'eine Sache' : `${offen} Sachen`}, die nicht bis morgen warten`}
        </span>
      </div>

      {/**
       * **`@container`** (24.10.2026, Nils am Telefon: *„Bei Klopft an kann man nicht gut lesen,
       * was gemeint ist"* – vom Titel blieb „HOW …"). Ob eine Zeile reicht, hängt an der Breite
       * dieser Karte, nicht am Bildschirm: in der Seitenspalte von My Work ist sie auch am Rechner
       * schmal. Unter 36rem steht der Titel deshalb in einer eigenen Zeile (bis zu zwei), die Art
       * darüber, der Hinweis darunter; breiter bleibt es eine Zeile wie bisher.
       */}
      <div className="@container overflow-hidden rounded-xl border border-line2 border-l-[3px] border-l-pool-sofort bg-surface shadow-task">
        {sichtbar.map((m) => {
          /**
           * **Die Grenze des Hakens, einmal ausgerechnet.** `seit` trägt bei „Antwort" den
           * Zeitpunkt des Kommentars – genau bis dahin gilt „gelesen", damit nicht auch als
           * gelesen zählt, was in der Zwischenzeit hereinkam. Hier und nicht im `onClick`,
           * weil eine Prüfung im JSX für den Rückruf darunter nicht mehr gilt.
           */
          const abhakenBis = KLOPF_ABHAKBAR.includes(m.art) ? m.seit : null

          return (
            <div
              key={`${m.art}-${m.taskId}`}
              className={cn(
                /**
                 * `flex-wrap` unter `sm` ([Z-10](../../../../../docs/phasen/zusatz-10-mobil.md)):
                 * der Hinweis („Esther Broekhuis wartet auf deine Abnahme") ist rund 230 px
                 * lang und schrumpfte nicht – der Knopf am rechten Rand wurde abgeschnitten.
                 * Jetzt rutscht er auf eine zweite Zeile, statt aus dem Bild zu laufen.
                 */
                'flex items-center gap-2.5 px-3.5 py-2 text-[13px] not-first:border-t not-first:border-line',
                /** Im Nachlauf blass: abgehakt, aber noch da – und der Klick zurücknehmbar. */
                m.gelesen && 'opacity-55',
              )}
            >
              <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5 @xl:flex-nowrap @xl:items-center @xl:gap-2.5">
                <span
                  className={`order-1 shrink-0 text-[11px] font-bold tracking-[0.04em] uppercase ${WORT_STIL[m.art]}`}
                >
                  {KLOPF_WORT[m.art]}
                </span>
                {/* Schmal steht der Hinweis unter dem Titel, breit dahinter (Z-10). */}
                <span className="order-4 min-w-0 basis-full truncate text-[11.5px] text-muted @xl:order-3 @xl:max-w-[45%] @xl:basis-auto @xl:flex-none">
                  {m.hinweis}
                </span>
                <AufgabenVerweis
                  aufgabeId={m.taskId}
                  className="order-3 min-w-0 basis-full cursor-pointer text-left text-[14px] leading-snug hover:text-accent hover:underline @xl:order-2 @xl:basis-auto @xl:flex-1 @xl:text-[13px]"
                  title={m.titel}
                >
                  <span className="line-clamp-2 @xl:line-clamp-1">{m.titel}</span>
                </AufgabenVerweis>
              </div>

              {/*
                Der Haken steht **vor** dem Knopf, nicht dahinter: rechtsbündig steht in jeder
                Zeile dieser Zone eine Entscheidung, und der Haken ist keine. Nur bei „Antwort"
                und „Erwähnung" – siehe den Kopf dieser Datei.
              */}
              {abhakenBis && (
                <button
                  type="button"
                  aria-pressed={m.gelesen}
                  aria-label={`${m.gelesen ? 'Häkchen zurücknehmen' : 'Als gelesen abhaken'}: ${m.titel}`}
                  title={
                    m.gelesen
                      ? 'Wieder als ungelesen führen'
                      : 'Als gelesen abhaken – blendet sich in zehn Minuten aus'
                  }
                  onClick={() =>
                    gelesenSetzen({
                      taskId: m.taskId,
                      bis: abhakenBis.toISOString(),
                      gelesen: !m.gelesen,
                    })
                  }
                  className={cn(
                    'flex size-[17px] shrink-0 cursor-pointer items-center justify-center rounded-[4px] border text-[10px] leading-none max-sm:size-7 max-sm:rounded-md max-sm:text-[13px]',
                    m.gelesen
                      ? 'border-accent bg-accent text-white'
                      : 'border-line2 text-transparent hover:border-accent hover:text-accent-deep',
                  )}
                >
                  ✓
                </button>
              )}

              {/*
                Genau ein Knopf je Zeile. Zwei wären eine Bedienleiste, und die gehört ins
                Detail – hier soll man entscheiden, nicht verwalten.
              */}
              {m.art === 'sofort' ? (
                <Button size="sm" variant="primary" onClick={() => ziehen(m.taskId)}>
                  Ziehen
                </Button>
              ) : m.art === 'frist' && m.startbar ? (
                /* Eine überfällige Aufgabe, die mir gehört und noch nicht läuft: der eine Knopf
                   ist „Starten" (E-337). Ansehen geht weiter über den Titel. */
                <Button size="sm" variant="primary" onClick={() => ziehen(m.taskId)}>
                  Starten
                </Button>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => oeffnen(m.taskId)}>
                  {m.art === 'abnahme' ? 'Abnehmen' : 'Ansehen'}
                </Button>
              )}
            </div>
          )
        })}

        {rest > 0 && (
          <button
            type="button"
            onClick={() => setAlle(true)}
            className="w-full cursor-pointer border-t border-line px-3.5 py-[7px] text-left text-xs text-muted hover:bg-page"
          >
            + {rest} weitere
          </button>
        )}
      </div>
    </section>
  )
}

/**
 * Fünf Sorten, aber alle Farben aus den vorhandenen Tokens – keine neue Skala.
 *
 * **`erwaehnung` teilt sich die Farbe mit `antwort`**, weil beides Nachrichten sind; getrennt
 * werden sie durch das Wort, nicht durch den Ton. Eine sechste Farbe hätte die Zone in eine
 * Legende verwandelt.
 */
const WORT_STIL: Record<Klopfmeldung['art'], string> = {
  sofort: 'text-pool-sofort',
  antwort: 'text-accent-deep',
  erwaehnung: 'text-accent-deep',
  frist: 'text-warn',
  abnahme: 'text-pool-projekte',
  /** Wie die Frist, weil es dieselbe Sache in einem früheren Stadium ist – eine Uhr, die läuft. */
  vorwarnung: 'text-warn',
}
